import { useT } from '@/i18n/provider';
import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };

  return (
    <div className={`my-4 rounded-xl border-l-4 px-4 py-3 ${styles[type]}`}>
      <span className="font-semibold text-sm">{labels[type]}:</span>{' '}
      <span className="text-sm">{children}</span>
    </div>
  );
}

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'how-it-works', label: 'How It Works' },
  { id: 'service-discovery', label: 'Service Discovery' },
  { id: 'baselines', label: 'Baselines' },
  { id: 'baseline-computation', label: 'Baseline Computation', level: 1 },
  { id: 'time-slot-baselines', label: 'Time-Slot Baselines', level: 1 },
  { id: 'detection-algorithm', label: 'Detection Algorithm' },
  { id: 'modified-z-score', label: 'Modified Z-Score', level: 1 },
  { id: 'metric-correlation', label: 'Metric Correlation', level: 1 },
  { id: 'severity-levels', label: 'Severity Levels', level: 1 },
  { id: 'configuring-services', label: 'Configuring Services' },
  { id: 'sensitivity', label: 'Sensitivity', level: 1 },
  { id: 'detection-intervals', label: 'Detection Intervals', level: 1 },
  { id: 'urgent-detection', label: 'Urgent Detection', level: 1 },
  { id: 'tracked-metrics', label: 'Tracked Metrics' },
  { id: 'managing-anomalies', label: 'Managing Anomalies' },
  { id: 'anomaly-lifecycle', label: 'Anomaly Lifecycle', level: 1 },
  { id: 'feedback', label: 'Feedback & Learning', level: 1 },
  { id: 'ai-investigation', label: 'AI Investigation' },
  { id: 'known-patterns', label: 'Known Patterns', level: 1 },
  { id: 'notifications', label: 'Notifications' },
  { id: 'response-channels', label: 'Response Channels', level: 1 },
  { id: 'severity-filtering', label: 'Severity Filtering', level: 1 },
  { id: 'dependencies', label: 'Service Dependencies' },
  { id: 'impact-analysis', label: 'Impact Analysis', level: 1 },
  { id: 'api-reference', label: 'API Reference' },
  { id: 'api-services', label: 'Service Endpoints', level: 1 },
  { id: 'api-anomalies', label: 'Anomaly Endpoints', level: 1 },
  { id: 'api-channels', label: 'Response Channel Endpoints', level: 1 },
  { id: 'api-dependencies', label: 'Dependency Endpoints', level: 1 },
];

export function AnomalyDetectionPage() {
  useDocumentMeta({ title: 'Anomaly Detection: LogPulse Docs', description: 'AI-powered anomaly detection with z-score baselines, configurable sensitivity, and multi-channel notifications.' });

  const t = useT();

  return (
    <div className="flex gap-8">
      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Page header */}
        <div className="mb-8">
          <div className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-2">
            {t('docs.nav.anomalyDetection')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            {t('docs.anomaly.title')}
          </h1>
          <P>{t('docs.anomaly.intro')}</P>
        </div>

        {/* Overview */}
        <Section id="overview" title={t('docs.anomaly.overview.title')}>
          <P>{t('docs.anomaly.overview.description')}</P>
          <div className="my-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: t('docs.anomaly.overview.card1Title'), desc: t('docs.anomaly.overview.card1Desc'), color: 'text-info-500' },
              { title: t('docs.anomaly.overview.card2Title'), desc: t('docs.anomaly.overview.card2Desc'), color: 'text-warning-500' },
              { title: t('docs.anomaly.overview.card3Title'), desc: t('docs.anomaly.overview.card3Desc'), color: 'text-success-500' },
              { title: t('docs.anomaly.overview.card4Title'), desc: t('docs.anomaly.overview.card4Desc'), color: 'text-primary-500' },
            ].map((card) => (
              <div key={card.title} className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                <h4 className={`font-semibold text-sm mb-1 ${card.color}`}>{card.title}</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">{card.desc}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* How It Works */}
        <Section id="how-it-works" title={t('docs.anomaly.howItWorks.title')}>
          <P>{t('docs.anomaly.howItWorks.description')}</P>
          <div className="my-6 space-y-4">
            {[
              { step: '1', title: t('docs.anomaly.howItWorks.step1Title'), desc: t('docs.anomaly.howItWorks.step1Desc') },
              { step: '2', title: t('docs.anomaly.howItWorks.step2Title'), desc: t('docs.anomaly.howItWorks.step2Desc') },
              { step: '3', title: t('docs.anomaly.howItWorks.step3Title'), desc: t('docs.anomaly.howItWorks.step3Desc') },
              { step: '4', title: t('docs.anomaly.howItWorks.step4Title'), desc: t('docs.anomaly.howItWorks.step4Desc') },
              { step: '5', title: t('docs.anomaly.howItWorks.step5Title'), desc: t('docs.anomaly.howItWorks.step5Desc') },
            ].map((item) => (
              <div key={item.step} className="flex gap-4 items-start">
                <div className="shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-sm font-bold text-primary-700 dark:text-primary-300">
                  {item.step}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{item.title}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Service Discovery */}
        <Section id="service-discovery" title={t('docs.anomaly.serviceDiscovery.title')}>
          <P>{t('docs.anomaly.serviceDiscovery.description')}</P>
          <P>{t('docs.anomaly.serviceDiscovery.criteria')}</P>
          <Table
            headers={['Status', 'Description']}
            rows={[
              ['discovered', t('docs.anomaly.serviceDiscovery.statusDiscovered')],
              ['active', t('docs.anomaly.serviceDiscovery.statusActive')],
              ['paused', t('docs.anomaly.serviceDiscovery.statusPaused')],
              ['ignored', t('docs.anomaly.serviceDiscovery.statusIgnored')],
            ]}
          />
          <Callout type="tip">{t('docs.anomaly.serviceDiscovery.tip')}</Callout>
        </Section>

        {/* Baselines */}
        <Section id="baselines" title={t('docs.anomaly.baselines.title')}>
          <P>{t('docs.anomaly.baselines.description')}</P>

          <SubSection id="baseline-computation" title={t('docs.anomaly.baselines.computation.title')}>
            <P>{t('docs.anomaly.baselines.computation.description')}</P>
            <Table
              headers={['Statistic', 'Description']}
              rows={[
                ['Median', t('docs.anomaly.baselines.statMedian')],
                ['Q1 (25th percentile)', t('docs.anomaly.baselines.statQ1')],
                ['Q3 (75th percentile)', t('docs.anomaly.baselines.statQ3')],
                ['IQR', t('docs.anomaly.baselines.statIQR')],
                ['Mean', t('docs.anomaly.baselines.statMean')],
                ['Standard Deviation', t('docs.anomaly.baselines.statStdDev')],
                ['Sample Count', t('docs.anomaly.baselines.statSampleCount')],
              ]}
            />
          </SubSection>

          <SubSection id="time-slot-baselines" title={t('docs.anomaly.baselines.timeSlot.title')}>
            <P>{t('docs.anomaly.baselines.timeSlot.description')}</P>
            <P>{t('docs.anomaly.baselines.timeSlot.example')}</P>
            <Callout type="info">{t('docs.anomaly.baselines.timeSlot.note')}</Callout>
          </SubSection>
        </Section>

        {/* Detection Algorithm */}
        <Section id="detection-algorithm" title={t('docs.anomaly.detection.title')}>
          <P>{t('docs.anomaly.detection.description')}</P>

          <SubSection id="modified-z-score" title={t('docs.anomaly.detection.zScore.title')}>
            <P>{t('docs.anomaly.detection.zScore.description')}</P>
            <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
              <pre className="p-4 bg-gray-950 text-gray-100 text-sm overflow-x-auto font-mono leading-relaxed">
                <code>
                  <span className="text-gray-500">{'# Modified Z-Score formula'}</span>{'\n'}
                  <span className="text-cyan-400">modified_z_score</span>{' '}<span className="text-gray-400">=</span>{' '}<span className="text-gray-300">0.6745</span>{' '}<span className="text-gray-400">*</span>{' '}<span className="text-gray-400">(</span><span className="text-cyan-400">value</span>{' '}<span className="text-gray-400">-</span>{' '}<span className="text-cyan-400">median</span><span className="text-gray-400">)</span>{' '}<span className="text-gray-400">/</span>{' '}<span className="text-cyan-400">iqr</span>
                </code>
              </pre>
            </div>
            <P>{t('docs.anomaly.detection.zScore.explanation')}</P>
          </SubSection>

          <SubSection id="metric-correlation" title={t('docs.anomaly.detection.correlation.title')}>
            <P>{t('docs.anomaly.detection.correlation.description')}</P>
            <Callout type="info">{t('docs.anomaly.detection.correlation.note')}</Callout>
          </SubSection>

          <SubSection id="severity-levels" title={t('docs.anomaly.detection.severity.title')}>
            <P>{t('docs.anomaly.detection.severity.description')}</P>
            <Table
              headers={['Severity', 'Z-Score Threshold', 'Description']}
              rows={[
                ['Low', '> 1.5', t('docs.anomaly.detection.severity.low')],
                ['Medium', '> 2.5', t('docs.anomaly.detection.severity.medium')],
                ['High', '> 3.5', t('docs.anomaly.detection.severity.high')],
                ['Critical', '> 5.0', t('docs.anomaly.detection.severity.critical')],
              ]}
            />
          </SubSection>
        </Section>

        {/* Configuring Services */}
        <Section id="configuring-services" title={t('docs.anomaly.config.title')}>
          <P>{t('docs.anomaly.config.description')}</P>

          <SubSection id="sensitivity" title={t('docs.anomaly.config.sensitivity.title')}>
            <P>{t('docs.anomaly.config.sensitivity.description')}</P>
            <Table
              headers={['Level', 'Behavior']}
              rows={[
                ['Low', t('docs.anomaly.config.sensitivity.low')],
                ['Medium (default)', t('docs.anomaly.config.sensitivity.medium')],
                ['High', t('docs.anomaly.config.sensitivity.high')],
              ]}
            />
            <P>{t('docs.anomaly.config.sensitivity.perMetric')}</P>
          </SubSection>

          <SubSection id="detection-intervals" title={t('docs.anomaly.config.intervals.title')}>
            <P>{t('docs.anomaly.config.intervals.description')}</P>
            <Table
              headers={['Setting', 'Options', 'Default']}
              rows={[
                ['Detection Interval', '30s, 1m, 2m, 5m', '1m'],
                ['Detection Lookback', '5m, 10m, 15m, 30m', '10m'],
              ]}
            />
            <Callout type="tip">{t('docs.anomaly.config.intervals.tip')}</Callout>
          </SubSection>

          <SubSection id="urgent-detection" title={t('docs.anomaly.config.urgent.title')}>
            <P>{t('docs.anomaly.config.urgent.description')}</P>
            <Table
              headers={['Setting', 'Description', 'Default']}
              rows={[
                ['Enabled', t('docs.anomaly.config.urgent.enabled'), 'Off'],
                ['Error Rate Threshold', t('docs.anomaly.config.urgent.threshold'), '50%'],
                ['Min Batch Size', t('docs.anomaly.config.urgent.batchSize'), '100'],
              ]}
            />
          </SubSection>
        </Section>

        {/* Tracked Metrics */}
        <Section id="tracked-metrics" title={t('docs.anomaly.metrics.title')}>
          <P>{t('docs.anomaly.metrics.description')}</P>
          <Table
            headers={['Metric', 'Description', 'Unit']}
            rows={[
              ['log_count', t('docs.anomaly.metrics.logCount'), 'count / 5m'],
              ['error_count', t('docs.anomaly.metrics.errorCount'), 'count / 5m'],
              ['error_rate', t('docs.anomaly.metrics.errorRate'), '%'],
              ['warn_count', t('docs.anomaly.metrics.warnCount'), 'count / 5m'],
              ['warn_rate', t('docs.anomaly.metrics.warnRate'), '%'],
              ['info_count', t('docs.anomaly.metrics.infoCount'), 'count / 5m'],
              ['info_rate', t('docs.anomaly.metrics.infoRate'), '%'],
            ]}
          />
          <Callout type="info">{t('docs.anomaly.metrics.note')}</Callout>
        </Section>

        {/* Managing Anomalies */}
        <Section id="managing-anomalies" title={t('docs.anomaly.managing.title')}>
          <P>{t('docs.anomaly.managing.description')}</P>

          <SubSection id="anomaly-lifecycle" title={t('docs.anomaly.managing.lifecycle.title')}>
            <P>{t('docs.anomaly.managing.lifecycle.description')}</P>
            <Table
              headers={['Status', 'Description']}
              rows={[
                ['Active', t('docs.anomaly.managing.lifecycle.active')],
                ['Acknowledged', t('docs.anomaly.managing.lifecycle.acknowledged')],
                ['Resolved', t('docs.anomaly.managing.lifecycle.resolved')],
                ['Dismissed', t('docs.anomaly.managing.lifecycle.dismissed')],
              ]}
            />
          </SubSection>

          <SubSection id="feedback" title={t('docs.anomaly.managing.feedback.title')}>
            <P>{t('docs.anomaly.managing.feedback.description')}</P>
            <Table
              headers={['Feedback', 'Effect']}
              rows={[
                ['True Positive', t('docs.anomaly.managing.feedback.truePositive')],
                ['False Positive', t('docs.anomaly.managing.feedback.falsePositive')],
              ]}
            />
            <Callout type="tip">{t('docs.anomaly.managing.feedback.tip')}</Callout>
          </SubSection>
        </Section>

        {/* AI Investigation */}
        <Section id="ai-investigation" title={t('docs.anomaly.ai.title')}>
          <P>{t('docs.anomaly.ai.description')}</P>
          <P>{t('docs.anomaly.ai.capabilities')}</P>
          <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 text-sm space-y-1.5 mb-4 ml-2">
            <li>{t('docs.anomaly.ai.cap1')}</li>
            <li>{t('docs.anomaly.ai.cap2')}</li>
            <li>{t('docs.anomaly.ai.cap3')}</li>
            <li>{t('docs.anomaly.ai.cap4')}</li>
            <li>{t('docs.anomaly.ai.cap5')}</li>
          </ul>

          <SubSection id="known-patterns" title={t('docs.anomaly.ai.patterns.title')}>
            <P>{t('docs.anomaly.ai.patterns.description')}</P>
            <Table
              headers={['Pattern Type', 'Description', 'Example']}
              rows={[
                ['time_based', t('docs.anomaly.ai.patterns.timeBased'), t('docs.anomaly.ai.patterns.timeBasedExample')],
                ['event_based', t('docs.anomaly.ai.patterns.eventBased'), t('docs.anomaly.ai.patterns.eventBasedExample')],
                ['metric_correlation', t('docs.anomaly.ai.patterns.correlation'), t('docs.anomaly.ai.patterns.correlationExample')],
              ]}
            />
            <Callout type="info">{t('docs.anomaly.ai.patterns.note')}</Callout>
          </SubSection>
        </Section>

        {/* Notifications */}
        <Section id="notifications" title={t('docs.anomaly.notifications.title')}>
          <P>{t('docs.anomaly.notifications.description')}</P>

          <SubSection id="response-channels" title={t('docs.anomaly.notifications.channels.title')}>
            <P>{t('docs.anomaly.notifications.channels.description')}</P>
            <Table
              headers={['Channel', 'Configuration']}
              rows={[
                ['Email', t('docs.anomaly.notifications.channels.email')],
                ['Slack', t('docs.anomaly.notifications.channels.slack')],
                ['Webhook', t('docs.anomaly.notifications.channels.webhook')],
                ['PagerDuty', t('docs.anomaly.notifications.channels.pagerduty')],
              ]}
            />
          </SubSection>

          <SubSection id="severity-filtering" title={t('docs.anomaly.notifications.filtering.title')}>
            <P>{t('docs.anomaly.notifications.filtering.description')}</P>
            <Callout type="tip">{t('docs.anomaly.notifications.filtering.tip')}</Callout>
          </SubSection>
        </Section>

        {/* Service Dependencies */}
        <Section id="dependencies" title={t('docs.anomaly.dependencies.title')}>
          <P>{t('docs.anomaly.dependencies.description')}</P>
          <Table
            headers={['Dependency Type', 'Description']}
            rows={[
              ['api', t('docs.anomaly.dependencies.typeApi')],
              ['database', t('docs.anomaly.dependencies.typeDatabase')],
              ['queue', t('docs.anomaly.dependencies.typeQueue')],
              ['cache', t('docs.anomaly.dependencies.typeCache')],
              ['storage', t('docs.anomaly.dependencies.typeStorage')],
              ['auth', t('docs.anomaly.dependencies.typeAuth')],
            ]}
          />

          <SubSection id="impact-analysis" title={t('docs.anomaly.dependencies.impact.title')}>
            <P>{t('docs.anomaly.dependencies.impact.description')}</P>
            <Callout type="info">{t('docs.anomaly.dependencies.impact.note')}</Callout>
          </SubSection>
        </Section>

        {/* API Reference */}
        <Section id="api-reference" title={t('docs.anomaly.api.title')}>
          <P>{t('docs.anomaly.api.description')}</P>

          <SubSection id="api-services" title={t('docs.anomaly.api.services.title')}>
            <Table
              headers={['Method', 'Endpoint', 'Description']}
              rows={[
                ['GET', '/api/v1/detect/services', t('docs.anomaly.api.services.list')],
                ['POST', '/api/v1/detect/services', t('docs.anomaly.api.services.create')],
                ['PUT', '/api/v1/detect/services/:id', t('docs.anomaly.api.services.update')],
                ['POST', '/api/v1/detect/services/:id/activate', t('docs.anomaly.api.services.activate')],
                ['GET', '/api/v1/detect/services/health', t('docs.anomaly.api.services.health')],
                ['GET', '/api/v1/detect/services/:id/baseline-status', t('docs.anomaly.api.services.baselineStatus')],
              ]}
            />
          </SubSection>

          <SubSection id="api-anomalies" title={t('docs.anomaly.api.anomalies.title')}>
            <Table
              headers={['Method', 'Endpoint', 'Description']}
              rows={[
                ['GET', '/api/v1/detect/anomalies', t('docs.anomaly.api.anomalies.list')],
                ['GET', '/api/v1/detect/anomalies/:id', t('docs.anomaly.api.anomalies.get')],
                ['PUT', '/api/v1/detect/anomalies/:id/acknowledge', t('docs.anomaly.api.anomalies.acknowledge')],
                ['PUT', '/api/v1/detect/anomalies/:id/dismiss', t('docs.anomaly.api.anomalies.dismiss')],
                ['PUT', '/api/v1/detect/anomalies/:id/feedback', t('docs.anomaly.api.anomalies.feedback')],
                ['POST', '/api/v1/detect/anomalies/:id/investigate', t('docs.anomaly.api.anomalies.investigate')],
                ['GET', '/api/v1/detect/anomalies/timeline', t('docs.anomaly.api.anomalies.timeline')],
              ]}
            />
          </SubSection>

          <SubSection id="api-channels" title={t('docs.anomaly.api.channels.title')}>
            <Table
              headers={['Method', 'Endpoint', 'Description']}
              rows={[
                ['GET', '/api/v1/detect/response-channels', t('docs.anomaly.api.channels.list')],
                ['POST', '/api/v1/detect/response-channels', t('docs.anomaly.api.channels.create')],
                ['PUT', '/api/v1/detect/response-channels/:id', t('docs.anomaly.api.channels.update')],
                ['DELETE', '/api/v1/detect/response-channels/:id', t('docs.anomaly.api.channels.delete')],
              ]}
            />
          </SubSection>

          <SubSection id="api-dependencies" title={t('docs.anomaly.api.dependencies.title')}>
            <Table
              headers={['Method', 'Endpoint', 'Description']}
              rows={[
                ['GET', '/api/v1/detect/dependencies', t('docs.anomaly.api.dependencies.list')],
                ['POST', '/api/v1/detect/dependencies', t('docs.anomaly.api.dependencies.create')],
                ['DELETE', '/api/v1/detect/dependencies/:id', t('docs.anomaly.api.dependencies.delete')],
                ['POST', '/api/v1/detect/dependencies/:id/confirm', t('docs.anomaly.api.dependencies.confirm')],
              ]}
            />
          </SubSection>
        </Section>
      </div>

      <TableOfContents items={tocItems} />
    </div>
  );
}
